import { useCallback, useMemo } from 'react';

import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { useUserProfile } from '@/modules/settings/hooks/account/use-user-profile';

/**
 * Hook for evaluating user permissions based on their policies
 * Provides utilities to check single or multiple permissions
 *
 * FullAccess policy grants all permissions automatically
 */
export function usePermissions() {
  const { userProfile, isLoading, error } = useUserProfile();

  const policies = useMemo(() => new Set(userProfile?.policies ?? []), [userProfile?.policies]);

  const hasFullAccess = useMemo(() => policies.has(OpsiumPolicy.FullAccess), [policies]);

  /**
   * Checks if the user has a specific permission
   * Returns true if user has FullAccess or the specific policy
   */
  const hasPermission = useCallback(
    (policy: OpsiumPolicy): boolean => {
      if (hasFullAccess) return true;
      return policies.has(policy);
    },
    [policies, hasFullAccess]
  );

  /**
   * Checks if the user has ALL of the specified permissions
   * Returns true if user has FullAccess or all specified policies
   */
  const hasAllPermissions = useCallback(
    (requiredPolicies: OpsiumPolicy[]): boolean => {
      if (hasFullAccess) return true;
      return requiredPolicies.every(policy => policies.has(policy));
    },
    [policies, hasFullAccess]
  );

  /**
   * Checks if the user has ANY of the specified permissions
   * Returns true if user has FullAccess or any of the specified policies
   */
  const hasAnyPermission = useCallback(
    (requiredPolicies: OpsiumPolicy[]): boolean => {
      if (hasFullAccess) return true;
      return requiredPolicies.some(policy => policies.has(policy));
    },
    [policies, hasFullAccess]
  );

  /**
   * Returns the list of permissions the user has from a given set
   * Returns all requested permissions if user has FullAccess
   */
  const getMatchingPermissions = useCallback(
    (requiredPolicies: OpsiumPolicy[]): OpsiumPolicy[] => {
      if (hasFullAccess) return requiredPolicies;
      return requiredPolicies.filter(policy => policies.has(policy));
    },
    [policies, hasFullAccess]
  );

  /**
   * Returns the list of permissions the user is missing from a given set
   * Returns empty array if user has FullAccess
   */
  const getMissingPermissions = useCallback(
    (requiredPolicies: OpsiumPolicy[]): OpsiumPolicy[] => {
      if (hasFullAccess) return [];
      return requiredPolicies.filter(policy => !policies.has(policy));
    },
    [policies, hasFullAccess]
  );

  return {
    isLoading,
    error,
    hasFullAccess,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    getMatchingPermissions,
    getMissingPermissions,
  };
}
